import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CeoTurnoverReason } from '../../../models/ceoTurnoverReason';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LutService } from '../../../services/lut.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-ceo-turnover-reason-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule
  ],
  templateUrl: './ceo-turnover-reason-list.component.html',
  styleUrl: './ceo-turnover-reason-list.component.scss'
})
export class CeoTurnoverReasonListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  ceoTurnoverReasons = signal<CeoTurnoverReason[]>([]);

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadCeoTurnoverReasons();
  }

  loadCeoTurnoverReasons(): void {
    this.spinnerService.show();
    this.lutService.getCeoTurnoverReasons().subscribe({
      next: (data) => {
        // Sort alphabetically by name before setting
        const sortedData = this.sortCeoTurnoverReasons(data);
        this.ceoTurnoverReasons.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading CEO turnover reasons:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortCeoTurnoverReasons(reasons: CeoTurnoverReason[]): CeoTurnoverReason[] {
    return [...reasons].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateCeoTurnoverReason(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(ceoTurnoverReason: CeoTurnoverReason): void {
  }

  onEditCeoTurnoverReason(ceoTurnoverReason: CeoTurnoverReason): void {
  }

  onDeleteCeoTurnoverReason(ceoTurnoverReason: CeoTurnoverReason): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(ceoTurnoverReason.name)
      .subscribe(result => {
        if (result) {
          // Store original list for rollback in case of error
          const originalReasons = this.ceoTurnoverReasons();

          // 1. Optimistically remove from local state immediately
          this.ceoTurnoverReasons.update(reasons => reasons.filter(r => r.id !== ceoTurnoverReason.id));

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteCeoTurnoverReason(ceoTurnoverReason.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('CEO turnover reason deleted successfully');
              // 3. On success: Already removed from UI, nothing more to do
            },
            error: (error) => {
              console.error('Error deleting CEO turnover reason:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original list
              this.ceoTurnoverReasons.set(originalReasons);
              this.toast.error(`<strong>Failed to delete CEO Turnover Reason:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: CeoTurnoverReason, newName: string} | {item: CeoTurnoverReason, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };

    // Prepare updated item
    let updatedItem: CeoTurnoverReason;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    // 1. Optimistically update local state immediately and re-sort
    this.ceoTurnoverReasons.update(reasons => {
      const updated = reasons.map(r => r.id === event.item.id ? updatedItem : r);
      return this.sortCeoTurnoverReasons(updated);
    });

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateCeoTurnoverReason(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('CEO turnover reason updated successfully');
        // 3. On success: Update with server response and re-sort to maintain alphabetical order
        this.ceoTurnoverReasons.update(reasons => {
          const updated = reasons.map(r => r.id === event.item.id ? response : r);
          return this.sortCeoTurnoverReasons(updated);
        });
      },
      error: (error) => {
        console.error('Error updating CEO turnover reason:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.ceoTurnoverReasons.update(reasons =>
          reasons.map(r => r.id === event.item.id ? originalItem : r)
        );
        this.toast.error(`<strong>Failed to update CEO Turnover Reason:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const newReason: CeoTurnoverReason = { id: tempId, name: event['name'], systemReserved: false };

    // 1. Optimistically add to local state immediately and sort
    this.ceoTurnoverReasons.update(reasons => this.sortCeoTurnoverReasons([...reasons, newReason]));

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createCeoTurnoverReason({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('CEO turnover reason created successfully');
        // 3. On success: Replace temp item with server response and re-sort to maintain alphabetical order
        this.ceoTurnoverReasons.update(reasons => {
          const updated = reasons.map(r => r.id === tempId ? response : r);
          return this.sortCeoTurnoverReasons(updated);
        });
      },
      error: (error) => {
        console.error('Error creating CEO turnover reason:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.ceoTurnoverReasons.update(reasons =>
          reasons.filter(r => r.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create CEO Turnover Reason:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }
}
